import { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    Button,
    ButtonColor,
    ButtonSize,
    ButtonVariant,
    CopyIcon,
    Link,
    LinkVariant,
    MailIcon,
    Modal,
    ModalContentPosition,
    ModalContentSize,
    PureButton,
    ShareIcon,
    SocialWhatsAppIcon,
    useTimeoutSubscription,
} from '@jsmdg/yoshi';
import { type ProductDetailContext } from '../../types/types';
import { trackSocialShareAction, trackWhatsAppUnavailable } from './shareContainer.tracking';
import { TRACKING_LABEL, UTM_SOURCE } from './shareOptions';
import styles from './ShareContainer.module.scss';

const messages = defineMessages({
    copyMessage: { defaultMessage: 'Link kopieren' },
    copiedMessage: { defaultMessage: 'Link kopiert!' },
    mailMessage: { defaultMessage: 'Per E-Mail senden' },
    shareCTAMessage: { defaultMessage: 'Erlebnis teilen' },
    shareMailSubjectMessage: { defaultMessage: 'Sieh dir dieses Erlebnis an' },
    shareMessage: {
        defaultMessage: 'Sieh dir dieses Erlebnis bei {shareName} an!%0A%0A{shareUrl}',
    },
    shareWhatsApp: {
        defaultMessage: 'WhatsApp',
    },
    closeShareModal: {
        defaultMessage: 'Teilen Dialog schließen',
    },
    whatsAppErrorMessage: {
        defaultMessage:
            'Es scheint so, als wäre WhatsApp nicht auf deinem Smartphone installiert. Bitte nutze zum Teilen eine andere Methode.',
    },
});

type ShareContainerProps = {
    readonly productUrl: string;
    readonly isDisabled?: boolean;
};

const ShareContainer = ({ isDisabled = false, productUrl }: ShareContainerProps): JSX.Element => {
    const intl = useIntl();
    const { tenantConfig } = useFragmentContext<ProductDetailContext>();

    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isWhatsAppError, setIsWhatsAppError] = useState(false);
    const [isCopiedMessage, setIsCopiedMessage] = useState(false);
    const { cancelTimeout, timeoutIdRef } = useTimeoutSubscription();

    const createShareUrl = (utmSource: string, encodeUtmParameter?: boolean): string => {
        const currentDomain = typeof window !== 'undefined' && window.location?.hostname;
        const baseShareUrl = encodeURI(`${currentDomain as string}${productUrl}`);
        const utmParameter = `utm_source=${utmSource}&utm_medium=social_share`;

        return `${baseShareUrl}?${
            encodeUtmParameter ? encodeURIComponent(utmParameter) : utmParameter
        }`;
    };

    const handleRequestClose = (label: string): void => {
        trackSocialShareAction(label);
        setIsWhatsAppError(false);
        setIsShareModalOpen(false);
        setIsCopiedMessage(false);
    };

    const handleWhatsAppClick = (): void => {
        document.addEventListener(
            'visibilitychange',
            () => {
                cancelTimeout();
                handleRequestClose(TRACKING_LABEL.WHATS_APP);
            },
            { once: true },
        );

        timeoutIdRef.current = setTimeout(() => {
            setIsWhatsAppError(true);
            trackWhatsAppUnavailable();
        }, 3_000);

        window.open(
            `whatsapp://send?text=${intl.formatMessage(messages.shareMessage, {
                shareName: tenantConfig.shareTitle,
                shareUrl: createShareUrl(UTM_SOURCE.WHATS_APP, true),
            })}`,
            '_self',
            'noopener,noreferrer',
        );
    };

    return (
        <>
            <Button
                variant={ButtonVariant.Outlined}
                className="w-100 px-3x"
                onClick={() => setIsShareModalOpen(true)}
                size={ButtonSize.Large}
                iconLeft={<ShareIcon />}
                color={ButtonColor.Brand}
                disabled={isDisabled}
                a11yText={intl.formatMessage(messages.shareCTAMessage)}
            >
                <span>{intl.formatMessage(messages.shareCTAMessage)}</span>
            </Button>
            <Modal
                contentSize={ModalContentSize.HalfScreen}
                contentPosition={ModalContentPosition.Bottom}
                headline={intl.formatMessage(messages.shareCTAMessage)}
                isOpen={isShareModalOpen}
                modalAddendum={
                    isShareModalOpen
                        ? `${styles.isOpen} ${styles.shareModalAnimation}`
                        : styles.shareModalAnimation
                }
                onRequestClose={() => handleRequestClose(TRACKING_LABEL.CLOSE)}
                closeTimeoutMS={400}
                a11yCloseText={intl.formatMessage(messages.closeShareModal)}
            >
                <>
                    <PureButton
                        className={classNames(
                            styles.shareOptionsEntry,
                            'd-flex align-items-start w-100 py-2x pl-3x',
                            {
                                [styles.noBorder]: isWhatsAppError,
                            },
                        )}
                        onClick={() => handleWhatsAppClick()}
                    >
                        <SocialWhatsAppIcon className="mr-2x" />
                        {intl.formatMessage(messages.shareWhatsApp)}
                    </PureButton>
                    {isWhatsAppError && (
                        <span
                            className={classNames(
                                'd-flex w-100 pt-0 pl-8x pb-2x',
                                styles.shareOptionsEntry,
                                styles.whatsAppWarning,
                            )}
                        >
                            {intl.formatMessage(messages.whatsAppErrorMessage)}
                        </span>
                    )}
                    <PureButton
                        className={classNames(
                            styles.shareOptionsEntry,
                            'd-flex align-items-start w-100 py-2x pl-3x',
                        )}
                    >
                        <CopyIcon className="mr-2x" />
                        <CopyToClipboard
                            text={createShareUrl(UTM_SOURCE.COPY_LINK)}
                            onCopy={() => {
                                trackSocialShareAction(TRACKING_LABEL.COPY_LINK);
                                setIsCopiedMessage(true);
                            }}
                        >
                            {isCopiedMessage ? (
                                <span>{intl.formatMessage(messages.copiedMessage)}</span>
                            ) : (
                                <span>{intl.formatMessage(messages.copyMessage)}</span>
                            )}
                        </CopyToClipboard>
                    </PureButton>
                    <Link
                        className={classNames(styles.shareOptionsEntry, 'd-flex w-100 py-2x pl-3x')}
                        href={`mailto:?subject=${intl.formatMessage(
                            messages.shareMailSubjectMessage,
                        )}&body=${intl.formatMessage(messages.shareMessage, {
                            shareName: tenantConfig.shareTitle,
                            shareUrl: createShareUrl(UTM_SOURCE.MAIL, true),
                        })}`}
                        onClick={() => handleRequestClose(TRACKING_LABEL.EMAIL)}
                        variant={LinkVariant.Brand}
                    >
                        <MailIcon className="mr-2x" />
                        {intl.formatMessage(messages.mailMessage)}
                    </Link>
                </>
            </Modal>
        </>
    );
};

export { ShareContainer };

// eslint-disable-next-line import/no-default-export
export default ShareContainer;
